import { z } from 'zod'
import { REGIMES_CONTRAPARTE } from './contraparte'
import { TRATAMENTOS } from './produto'

/**
 * Calculadora de formação de preço (ADR 0030): uma venda por vez, para usar na frente do cliente.
 *
 * Dois modos. CONVERTER parte do que ele já vende ("hoje vendo por R$ 10.000") e devolve o preço a
 * praticar no híbrido. FORMAR parte do custo e da margem desejada e forma o preço do zero nos dois
 * regimes. Nos dois, o DAS sai da faixa real da empresa, o IBS/CBS entra por fora no fim, e o crédito
 * das compras ligadas àquela venda abate o caixa.
 *
 * É a mesma aritmética do motor (§10): centavos inteiros, alíquotas em ppm, nada de float.
 */

const Centavos = z.number().int().min(0)
const Ppm = z.number().int()

export const MODOS_CALCULADORA = ['CONVERTER', 'FORMAR'] as const
export type ModoCalculadora = (typeof MODOS_CALCULADORA)[number]

export const EntradaCalculadora = z.object({
  ano: z.number().int().min(2027).max(2033).default(2027),
  modo: z.enum(MODOS_CALCULADORA).default('CONVERTER'),
  /** CONVERTER: o valor pelo qual ele vende hoje (a nota de hoje, com tudo embutido) */
  valorVenda: Centavos.default(0),
  /** FORMAR: custo da mercadoria ou do insumo desta venda */
  custo: Centavos.default(0),
  /** FORMAR: despesas que acompanham o preço (comissão, frete, taxa de cartão), em ppm do preço */
  despesas: Ppm.min(0).max(900_000).default(0),
  /** FORMAR: margem desejada sobre o preço, em ppm */
  margem: Ppm.min(0).max(900_000).default(200_000),
  /** compras ligadas a esta venda: quanto e de quem (decide o crédito) */
  compra: Centavos.default(0),
  regimeFornecedor: z.enum(REGIMES_CONTRAPARTE).default('REGULAR'),
  /** o comprador aproveita o IBS/CBS da nota? empresa do regime regular sim; consumidor final não */
  clienteCredita: z.boolean().default(true),
  /** tratamento do produto: redução de alíquota da LC 214 (cesta básica, saúde, profissão intelectual…) */
  tratamento: z.enum(TRATAMENTOS).default('INTEGRAL'),
  /** quanto de crédito você quer entregar ao cliente: a conta devolve o aumento de nota necessário */
  creditoDesejado: Centavos.default(0),

  // ── empresa de regime regular (ADR 0033): aqui não há DAS, e os tributos de hoje vêm da nota ──
  /** o regime da empresa. SIMPLES compara puro × híbrido; PRESUMIDO e REAL comparam hoje × 2027 */
  regimeEmpresa: z.enum(['SIMPLES', 'PRESUMIDO', 'REAL']).default('SIMPLES'),
  /** PIS/COFINS de hoje sobre a venda (3,65% no Presumido, 9,25% no Real) */
  pisCofins: Ppm.min(0).max(300_000).default(36_500),
  /** ICMS destacado na venda */
  icms: Ppm.min(0).max(400_000).default(0),
  /** ISS da venda (serviço) */
  iss: Ppm.min(0).max(100_000).default(0),
  /** IPI da venda (indústria) */
  ipi: Ppm.min(0).max(400_000).default(0),
  /** ICMS destacado na compra — é o crédito que ela já dá hoje */
  icmsCompra: Ppm.min(0).max(400_000).default(0),
  /** o cliente credita o PIS/COFINS da sua nota? só quem é do Lucro Real credita */
  clienteCreditaPisCofins: z.boolean().default(false),
})
export type EntradaCalculadora = z.infer<typeof EntradaCalculadora>

/** Um preço possível, com tudo o que ele produz. */
export interface PontoCalculadora {
  /** preço sem o imposto por fora — o que se negocia */
  preco: number
  /** IBS/CBS destacado por fora */
  tributo: number
  /** o que vai na nota: preço + imposto */
  nota: number
  /**
   * O que a empresa recolhe POR DENTRO do preço nesta venda: no Simples é o DAS; no regime regular são
   * PIS/COFINS, ICMS, ISS e IPI hoje, e só o ICMS/ISS que resta da transição em 2027.
   */
  das: number
  /** a abertura do que está em `das`, para o regime regular (no Simples, tudo vai em `das`) */
  porDentro?: { pisCofins: number; icms: number; iss: number; ipi: number }
  /** crédito das compras ligadas a esta venda */
  credito: number
  /** o que sobra no caixa: preço − DAS + crédito */
  caixa: number
  /** o que a operação custa ao cliente: a nota menos o que ele credita */
  custoCliente: number
  /** contra hoje, em ppm */
  variacaoNota: number
  variacaoCaixa: number
  variacaoCustoCliente: number
}

export interface SaidaCalculadora {
  modo: ModoCalculadora
  ano: number
  aliquotas: {
    cbs: number
    ibs: number
    /** CBS + IBS já com a redução do tratamento, que é o que entra na nota */
    total: number
    /** alíquota efetiva do Simples na faixa da empresa (0 no regime regular) */
    efetiva: number
    /** quanto da efetiva é CBS + IBS (sai do DAS no híbrido) */
    efetivaCbsIbs: number
    status: 'ESTIMATIVA' | 'OFICIAL'
    faixa: number
    rbt12: number
  }
  /** crédito das compras desta venda, e por que deu isso */
  credito: { valor: number; motivo: string }
  /** hoje, no Simples como está */
  hoje: PontoCalculadora
  /** o piso mantém o caixa de hoje; o teto deixa o custo do cliente igual; o meio divide */
  piso: PontoCalculadora
  meio: PontoCalculadora
  teto: PontoCalculadora
  /** mantendo a mesma nota de hoje (o cliente paga o mesmo total) */
  mesmaNota: PontoCalculadora
  /**
   * O número que serve para os próximos orçamentos: multiplique o preço de hoje por este fator para
   * chegar ao preço do piso (em ppm; 1.000.000 = sem mudança).
   */
  fatorPreco: number
  /** e o fator do valor final da nota, que é o que o cliente enxerga */
  fatorNota: number
  /** a cascata para mostrar ao cliente (só quando ele aproveita o crédito) */
  demonstracao: DemonstracaoCliente | null
  /** o crédito das compras da empresa virando desconto no preço (só quando há compra com crédito) */
  competitividade: Competitividade | null
  /** o regime usado na conta — a tela troca os rótulos por causa dele */
  regimeEmpresa: 'SIMPLES' | 'PRESUMIDO' | 'REAL'
  /** quanto da carga de hoje é tributo sobre consumo, para a abertura na tela */
  cargaHoje: { pisCofins: number; icms: number; iss: number; ipi: number; das: number; creditos: number; total: number }
  avisos: string[]
}

/** Uma linha da demonstração, na ordem em que se mostra ao cliente. */
export interface LinhaDemonstracao {
  rotulo: string
  valor: number
  /** '' para valor de partida, '-' e '+' para as parcelas, '=' para o resultado */
  sinal: '' | '+' | '-' | '='
  nota?: string
}

/**
 * A demonstração em cascata, do jeito que o cliente entende: a nota de hoje, o crédito que ele já tem
 * escondido dentro do DAS, o custo real dele — e então o híbrido com o preço fixado NESSE custo, para
 * ficar evidente que ele continua pagando o mesmo e passa a creditar o imposto inteiro.
 */
export interface DemonstracaoCliente {
  /** Simples como está: a nota é a de hoje e o crédito é só o que está dentro do DAS */
  puro: { linhas: LinhaDemonstracao[]; nota: number; credito: number; custoCliente: number }
  /** híbrido com o preço = custo real do cliente hoje (o ponto em que ele não paga nem um centavo a mais) */
  hibrido: { linhas: LinhaDemonstracao[]; preco: number; tributo: number; nota: number; credito: number; custoCliente: number }
  /** quanto a nota sobe para chegar lá, em ppm */
  aumentoNota: number
  /** crédito a mais que o cliente passa a ter */
  creditoAMais: number
  /** cada 1% a mais na nota gera este tanto de crédito */
  creditoPorPontoPercentual: number
  /** quanto a nota precisa subir para entregar o crédito pedido (null quando não se pediu) */
  paraCreditoDesejado: { desejado: number; nota: number; preco: number; aumentoNota: number } | null
}

/**
 * O outro lado da moeda: no híbrido a empresa passa a creditar o IBS/CBS das COMPRAS dela. Esse crédito é
 * dinheiro novo, e ela escolhe o que fazer com ele — embolsar ou abater do preço e ficar mais barata que
 * o concorrente que não optou.
 *
 * São duas escolhas, e a conta mostra as duas: manter o preço e **embolsar** o crédito (o caixa sobe), ou
 * **repassá-lo** no preço (o caixa fica o de hoje e o cliente compra mais barato).
 *
 * O abatimento é maior que o próprio crédito: baixando o preço, o DAS daquela venda cai junto. Cada real
 * de crédito paga `1 / (1 − DAS)` de desconto sem tirar nada do caixa.
 */
export interface Competitividade {
  /** crédito de IBS/CBS das compras ligadas a esta venda */
  credito: number
  /** de onde ele vem */
  motivo: string
  /** preço inalterado: o crédito fica no caixa da empresa (caixa de hoje + crédito) */
  semUsar: PontoCalculadora
  /** preço abatido: o crédito foi para o cliente, e o caixa continua o de hoje */
  usando: PontoCalculadora
  /** quanto dá para tirar do preço (maior que o crédito: o DAS cai junto) */
  abatimentoPreco: number
  /** o abatimento em ppm do preço sem usar */
  abatimentoPpm: number
  /** quanto cada R$ 1,00 de crédito vale de desconto, em ppm (1.000.000 = R$ 1,00) */
  alavanca: number
  /** quanto o cliente passa a pagar a menos, em reais e em ppm */
  economiaCliente: number
  economiaClientePpm: number
}
